/*
  Zugo Shop - Joykek basligi (Beymen duzeni)
  ------------------------------------------------------------------
  Temanin BasicHeader/StickyHeader bilesenlerinin yerini alir. Ayni isi
  yapar: `--header-height` degiskenini gunceller (mega menu bunun kadar
  asagi itilir) ve kaydirma davranisini yonetir.

  Arama, Shopify'in tahmine dayali arama ucundan HTML parcasi ceker
  (`section_id` ile bu bolumu render ettirir), boylece fiyat bicimi ve
  para birimi Liquid tarafinda dogru uretilir - tarayicida bicimlendirme
  tahmini yapilmaz.
*/

class ZugoHeader extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.stickyType = this.dataset.sticky || 'always';
    this.lastScroll = window.scrollY;
    this.threshold = 0;

    if (this.stickyType === 'none') {
      this.section?.classList.add('is-static');
    }

    this.measure();

    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this);
    }

    if (this.stickyType !== 'none') {
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    window.removeEventListener('scroll', this.onScroll);
  }

  measure() {
    requestAnimationFrame(() => {
      const height = Math.round(this.offsetHeight);
      const offsetTop = Math.round(this.section?.offsetTop || 0);
      document.documentElement.style.setProperty('--header-height', `${height}px`);
      document.documentElement.style.setProperty('--header-offset-top', `${offsetTop}px`);

      const topbar = this.querySelector('.zugo-header__topbar');
      this.threshold = topbar ? topbar.offsetHeight : 20;
    });
  }

  onScroll() {
    const current = window.scrollY;

    requestAnimationFrame(() => {
      this.classList.toggle('is-scrolled', current > this.threshold);

      if (this.stickyType === 'on-scroll-up') {
        // Menu acikken basligi kacirmak menuyu da goturur.
        const menuOpen = document.body.classList.contains('has-dropdown-menu');
        const goingDown = current > this.lastScroll && current > this.threshold + 80;
        this.section?.classList.toggle('is-hidden', goingDown && !menuOpen);
      }

      this.lastScroll = current;
    });
  }
}

if (!customElements.get('zugo-header')) {
  customElements.define('zugo-header', ZugoHeader);
}

class ZugoSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('.zugo-search__panel');
    this.results = this.querySelector('[data-results]');
    this.form = this.querySelector('form');
    this.defaultHTML = this.results ? this.results.innerHTML : '';
    this.cache = new Map();
    this.controller = null;

    if (!this.input || !this.results) return;

    this.onDocumentClick = this.onDocumentClick.bind(this);

    this.input.addEventListener('focus', () => this.open());
    this.input.addEventListener('click', () => this.open());
    this.input.addEventListener('input', this.debounce(() => this.onInput(), 250));
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close();
        this.input.blur();
      }
    });

    // Bos sorguda formu gondermek tum katalogu listeler; anlamsiz.
    this.form?.addEventListener('submit', (event) => {
      if (!this.query) event.preventDefault();
    });

    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  get query() {
    return this.input.value.trim();
  }

  open() {
    if (!this.results.innerHTML.trim()) return;
    this.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.classList.remove('is-open');
    this.input.setAttribute('aria-expanded', 'false');
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onInput() {
    const query = this.query;

    if (query.length < 2) {
      this.results.innerHTML = this.defaultHTML;
      this.open();
      return;
    }

    this.fetchResults(query);
  }

  buildUrl(query) {
    const url = new URL(this.dataset.suggestUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[limit]', this.dataset.limit || 6);
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('section_id', this.dataset.sectionId);
    return url.toString();
  }

  async fetchResults(query) {
    const url = this.buildUrl(query);

    if (this.cache.has(url)) {
      this.render(this.cache.get(url));
      return;
    }

    // Onceki istek hala ucuyorsa iptal et; yoksa yavas cevap hizliyi ezer.
    this.controller?.abort();
    this.controller = new AbortController();
    this.classList.add('is-loading');

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`arama istegi basarisiz: ${response.status}`);
      const text = await response.text();
      this.cache.set(url, text);
      this.render(text);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[zugo-search]', error);
        this.results.innerHTML = this.defaultHTML;
      }
    } finally {
      this.classList.remove('is-loading');
    }
  }

  render(text) {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const fresh = doc.getElementById(this.results.id);

    if (!fresh) {
      console.error(`[zugo-search] cevapta ${this.results.id} yok`);
      return;
    }

    this.results.innerHTML = fresh.innerHTML;
    this.tumSonuclarBaglantisi();

    // Cevap gec geldiyse ve kullanici bu arada baska yere tikladiysa panel
    // kendiliginden geri acilmamali.
    if (this.contains(document.activeElement)) this.open();
  }

  // Liquid tarafinda `predictive_search.terms` bos donebiliyor; o durumda
  // "tum sonuclari goster" baglantisi hic uretilmiyor. Burada tamamlanir.
  tumSonuclarBaglantisi() {
    const query = this.query;
    if (!query || !this.results.querySelector('.zugo-search__product, .zugo-search__term')) return;

    const url = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;
    let link = this.results.querySelector('.zugo-search__all');

    if (!link) {
      link = document.createElement('a');
      link.className = 'zugo-search__all';
      this.results.appendChild(link);
    }

    link.href = url;
    if (!link.textContent.trim()) {
      link.textContent = (this.dataset.allLabel || '').replace('%%TERIM%%', query);
    }
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('zugo-search')) {
  customElements.define('zugo-search', ZugoSearch);
}

/*
  Mega menu sag panelindeki video (Faruk 2026-08-16).

  `autoplay` KULLANILMIYOR: menu kapaliyken de indirip oynatmaya calisir,
  bosuna bant ve pil harcar. Bunun yerine acilis olayinda `play()`,
  kapanista `pause()` cagriliyor. Kaynak da `preload="none"` - video ancak
  menu ilk kez acildiginda inmeye basliyor.
*/
class ZugoMegaVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.kapsayici = this.closest('details');
    if (!this.video || !this.kapsayici) return;

    this.video.muted = true; // Safari otomatik oynatma icin ozellik yetmez
    this.onToggle = () => (this.kapsayici.open ? this.baslat() : this.video.pause());
    this.kapsayici.addEventListener('toggle', this.onToggle);

    // Tema bazi tetikleyicilerde `open` ozniteligini olay yaymadan degistiriyor
    this.gozlemci = new MutationObserver(this.onToggle);
    this.gozlemci.observe(this.kapsayici, { attributes: true, attributeFilter: ['open'] });
  }

  disconnectedCallback() {
    this.gozlemci?.disconnect();
    this.kapsayici?.removeEventListener('toggle', this.onToggle);
  }

  baslat() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.video.play().catch(() => {});
  }
}

if (!customElements.get('zugo-mega-video')) {
  customElements.define('zugo-mega-video', ZugoMegaVideo);
}
